/**
 * Sustena buy box.
 *
 * Owns the colour selector and the bundle selector on the product page, and writes the chosen pieces into
 * Horizon's own add-to-cart form rather than replacing it: the first piece goes in the form's `id` and
 * `quantity` inputs, the rest are appended as `items[n][id]` / `items[n][quantity]` hidden inputs, which
 * `/cart/add` accepts alongside `id`. Horizon submits, refreshes the cart and opens the drawer as usual.
 *
 * Vanilla: no imports, no framework, no theme runtime.
 */
(() => {
  /** Separator that cannot appear in an option value, so colour+size makes a safe map key. */
  const KEY_SEPARATOR = ' ';

  /** Hidden inputs this component owns, so it can clear its own and leave the rest of the form alone. */
  const LINE_INPUT_FLAG = 'data-sustena-bundle-line';

  class SustenaBuyBox extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;

      const source = this.querySelector('[data-sustena-buy-box-data]');
      if (!source || !source.textContent) return;

      try {
        this.data = JSON.parse(source.textContent);
      } catch (error) {
        console.warn('[sustena-buy-box] Could not read the product data', error);
        return;
      }

      this.ready = true;

      this.variantsByOption = new Map(this.data.variants.map((variant) => [key(variant.color, variant.size), variant]));
      this.colorsByName = new Map(this.data.colors.map((color) => [color.name, color]));

      this.color = this.dataset.initialColor;
      this.bundle = Number(this.dataset.defaultBundle) || 1;

      this.cards = Array.from(this.querySelectorAll('.sustena-bb__bundle'));
      this.rowsByBundle = new Map();

      for (const card of this.cards) {
        const quantity = Number(card.dataset.quantity);
        const rows = Array.from(card.querySelectorAll('.sustena-bb__row'));
        this.rowsByBundle.set(
          quantity,
          rows.map(() => ({ size: this.dataset.initialSize, color: this.color, touched: false }))
        );
      }

      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);

      // The express button lives in its own block further down the column, so the listener goes on
      // the section rather than on this element.
      this.section = this.closest('.shopify-section');
      this.section?.addEventListener('click', this.handleBuyNow);

      // The form is re-read after every cart update, in case Horizon morphed the buy buttons
      document.addEventListener('cart:update', this.syncForm);

      this.render({ moveGallery: false });
    }

    disconnectedCallback() {
      document.removeEventListener('cart:update', this.syncForm);
      this.section?.removeEventListener('click', this.handleBuyNow);
    }

    /**
     * Straight to paying, for the bundles Shopify's own express buttons cannot carry.
     *
     * A cart permalink (`/cart/id:qty,id:qty`) opens a checkout holding exactly those pieces, so what
     * is paid is what the cards show, whatever else was in the cart beforehand.
     * @param {Event} event
     */
    handleBuyNow = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest('[data-sustena-buy-now]');
      if (!(button instanceof HTMLButtonElement)) return;

      event.preventDefault();

      const { lines } = this.bundleLines();
      if (lines.length === 0) return;

      button.disabled = true;
      window.location.href = `/cart/${lines.map(([id, quantity]) => `${id}:${quantity}`).join(',')}`;
    };

    /** @param {MouseEvent} event */
    handleClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const swatch = target.closest('.sustena-bb__swatch');
      if (swatch instanceof HTMLElement) {
        const color = swatch.dataset.color;
        if (!color) return;

        const row = swatch.closest('.sustena-bb__row');

        if (row instanceof HTMLElement) {
          // A row swatch changes that piece only, and pins it against later changes of the main colour.
          // The gallery still jumps to that colour's photo: wherever a colour is picked, the photo follows.
          const card = row.closest('.sustena-bb__bundle');
          const quantity = Number(card instanceof HTMLElement ? card.dataset.quantity : NaN);
          const state = this.rowsByBundle.get(quantity)?.[Number(row.dataset.row)];
          if (!state) return;

          state.color = color;
          state.touched = true;
          this.render({ moveGallery: false });
          this.moveGalleryToColor(color);
        } else {
          this.selectColor(color);
        }
        return;
      }

      const card = target.closest('.sustena-bb__bundle');
      if (card instanceof HTMLElement && this.contains(card)) {
        const quantity = Number(card.dataset.quantity);
        if (quantity && quantity !== this.bundle) {
          this.bundle = quantity;
          this.render({ moveGallery: false });
        }
      }
    };

    /** @param {Event} event */
    handleChange = (event) => {
      const target = event.target;

      if (target instanceof HTMLSelectElement && target.classList.contains('sustena-bb__row-size')) {
        const row = target.closest('.sustena-bb__row');
        const card = target.closest('.sustena-bb__bundle');
        if (!(row instanceof HTMLElement) || !(card instanceof HTMLElement)) return;

        const state = this.rowsByBundle.get(Number(card.dataset.quantity))?.[Number(row.dataset.row)];
        if (!state) return;

        state.size = target.value;
        this.render({ moveGallery: false });
        return;
      }

      if (target instanceof HTMLInputElement && target.classList.contains('sustena-bb__bundle-input')) {
        this.bundle = Number(target.value) || this.bundle;
        this.render({ moveGallery: false });
      }
    };

    /**
     * Picks the main colour: it names the product, moves the gallery, and carries over to every piece the
     * buyer has not set by hand.
     * @param {string} color
     */
    selectColor(color) {
      this.color = color;

      for (const rows of this.rowsByBundle.values()) {
        for (const row of rows) {
          if (!row.touched) row.color = color;
        }
      }

      this.render({ moveGallery: true });
    }

    /** @param {{moveGallery: boolean}} options */
    render({ moveGallery }) {
      const name = this.querySelector('[data-ref="colorName"]');
      if (name) name.textContent = this.color;

      const mainSwatches = this.querySelector('[data-ref="mainSwatches"]');
      for (const swatch of mainSwatches?.querySelectorAll('.sustena-bb__swatch') ?? []) {
        if (swatch instanceof HTMLElement) {
          swatch.setAttribute('aria-checked', String(swatch.dataset.color === this.color));
        }
      }

      for (const card of this.cards) {
        const quantity = Number(card.dataset.quantity);
        const selected = quantity === this.bundle;
        card.dataset.selected = String(selected);

        const input = card.querySelector('.sustena-bb__bundle-input');
        if (input instanceof HTMLInputElement) input.checked = selected;

        const rows = this.rowsByBundle.get(quantity) ?? [];
        const rowElements = card.querySelectorAll('.sustena-bb__row');

        rowElements.forEach((rowElement, index) => {
          const state = rows[index];
          if (!state) return;

          const thumb = rowElement.querySelector('[data-ref="rowThumb"]');
          const source = this.colorsByName.get(state.color)?.thumb;
          if (thumb instanceof HTMLImageElement && source && thumb.getAttribute('src') !== source) {
            thumb.src = source;
          }

          const size = rowElement.querySelector('[data-ref="rowSize"]');
          if (size instanceof HTMLSelectElement) {
            if (size.value !== state.size) size.value = state.size;

            for (const option of size.options) {
              option.disabled = !this.variantFor(state.color, option.value)?.available;
            }
          }

          for (const swatch of rowElement.querySelectorAll('.sustena-bb__swatch')) {
            if (swatch instanceof HTMLElement) {
              swatch.setAttribute('aria-checked', String(swatch.dataset.color === state.color));
            }
          }
        });
      }

      if (moveGallery) this.moveGalleryToColor(this.color);

      this.syncForm();
    }

    /**
     * @param {string} color
     * @param {string} size
     * @returns {{id: number, available: boolean} | undefined}
     */
    variantFor(color, size) {
      return this.variantsByOption.get(key(color, size));
    }

    /** The pieces the selected card currently describes. */
    selectedRows() {
      return this.rowsByBundle.get(this.bundle) ?? [];
    }

    /**
     * The chosen pieces folded into cart lines: the same variant picked twice is one line of two, so
     * the cart, the form and the express link all say the same thing.
     * @returns {{lines: [number, number][], unavailable: boolean}}
     */
    bundleLines() {
      /** @type {Map<number, number>} */
      const quantities = new Map();
      let unavailable = false;

      for (const row of this.selectedRows()) {
        const variant = this.variantFor(row.color, row.size);
        if (!variant || !variant.available) {
          unavailable = true;
          continue;
        }
        quantities.set(variant.id, (quantities.get(variant.id) ?? 0) + 1);
      }

      return { lines: Array.from(quantities.entries()), unavailable };
    }

    /**
     * Writes the selection into Horizon's add-to-cart form. Same variant chosen twice becomes one line of
     * quantity two, so the cart shows what the buyer would expect.
     */
    syncForm = () => {
      const form = document.querySelector('product-form-component form');
      if (!(form instanceof HTMLFormElement)) return;

      for (const previous of form.querySelectorAll(`[${LINE_INPUT_FLAG}]`)) previous.remove();

      const { lines, unavailable } = this.bundleLines();
      const idInput = form.querySelector('input[name="id"]');
      const quantityInput = form.querySelector('[name="quantity"]');
      const [first, ...rest] = lines;

      if (idInput instanceof HTMLInputElement && first) {
        idInput.value = String(first[0]);
      }
      if (quantityInput instanceof HTMLInputElement && first && quantityInput.value !== String(first[1])) {
        quantityInput.value = String(first[1]);
        // Shopify's express button watches the form rather than reading it on click, so a silent write
        // would have it checking out one piece while the card says four.
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      rest.forEach(([id, quantity], index) => {
        form.appendChild(hiddenInput(`items[${index}][id]`, String(id)));
        form.appendChild(hiddenInput(`items[${index}][quantity]`, String(quantity)));
      });

      this.setAvailability(!unavailable && lines.length > 0);

      // One variant means the form is a plain id + quantity, which is the only shape Shopify's own
      // express buttons can carry. Anything mixed goes through our own button instead.
      if (this.section) {
        this.section.dataset.sustenaExpress = lines.length === 1 ? 'single' : 'mixed';
      }
    };

    /** @param {boolean} canAdd */
    setAvailability(canAdd) {
      const message = this.querySelector('[data-ref="unavailable"]');
      if (message instanceof HTMLElement) message.hidden = canAdd;

      for (const button of document.querySelectorAll('product-form-component button[name="add"]')) {
        if (button instanceof HTMLButtonElement) button.disabled = !canAdd;
      }
    }

    /**
     * Moves the gallery to the photo of a colour, by media id, so it works whatever order the gallery
     * rendered in and costs no request.
     * @param {string} color
     */
    moveGalleryToColor(color) {
      const mediaId = this.colorsByName.get(color)?.mediaId;
      if (!mediaId) return;

      const slideshow = document.querySelector('media-gallery slideshow-component');
      if (!slideshow) return;

      const slides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
      const index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${mediaId}"]`));
      if (index < 0) return;

      /** @type {{select?: (index: number) => void}} */ (slideshow).select?.(index);
    }
  }

  /**
   * @param {string} color
   * @param {string} size
   */
  function key(color, size) {
    return `${color}${KEY_SEPARATOR}${size}`;
  }

  /**
   * @param {string} name
   * @param {string} value
   */
  function hiddenInput(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.setAttribute(LINE_INPUT_FLAG, '');
    return input;
  }

  if (!customElements.get('sustena-buy-box')) {
    customElements.define('sustena-buy-box', SustenaBuyBox);
  }
})();
